/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {dlsColor} from '../../../theme/theme';
import {InfoTooltip} from '../../tooltip/InfoTooltip';
import {InputRow} from './InputRow';

export interface InputsRowData {
  id: string;
  label: string;
  value: string;
  isActive: boolean;
}

export interface InputsSectionProps {
  inputs: InputsRowData[];
  infoTooltip?: string;
}

const useStyles = makeStyles(() => ({
  container : {
    boxSizing     : 'border-box',
    display       : 'flex',
    flexDirection : 'column',
    alignItems    : 'center',
    width         : '100%',
    padding       : '16px',
    gap           : '16px',
    background    : dlsColor('white'),
    border        : `0.5px solid ${dlsColor('grey7')}`,
    borderRadius  : '8px'
  },
  titleRow : {
    display    : 'flex',
    alignItems : 'center',
    width      : '100%',
    gap        : '8px'
  },
  title : {
    fontFamily    : 'Roboto',
    fontWeight    : 700,
    fontSize      : '14px',
    lineHeight    : '16px',
    letterSpacing : '1px',
    textTransform : 'uppercase',
    color         : dlsColor('grey2')
  },
  rows : {
    display       : 'flex',
    flexDirection : 'column',
    alignItems    : 'flex-start',
    width         : '100%',
    gap           : '8px'
  }
}));

/**
 * InputsSection is the ION-7462 Inputs container: an "INPUTS" title (with
 * an info tooltip shown on hover, per Details.txt) followed by one
 * `InputRow` per input, 8px apart. Per Details.txt, this whole section
 * only renders when input data is available - callers (e.g.
 * `DiagnosticsAccordion`) are expected to omit this component entirely
 * rather than render it with an empty `inputs` array. The container has no
 * fixed height - it grows with the number of rows (and each row itself can
 * grow taller if its label/value text wraps), per the Figma "Inputs
 * Container" annotation.
 *
 * ```typescript
 *
 *   import {InputsSection} from
 *     '@ion/dls/src/components/assetdetails/current/InputsSection';
 *   ...
 *
 *   <InputsSection infoTooltip='Configurable inputs reported by this asset.'
 *     inputs={[
 *       {
 *         id: 'commuterUsage',
 *         label: 'Commuter Usage Button',
 *         value: 'Business miles',
 *         isActive: true
 *       },
 *       {id: 'blade', label: 'Blade', value: 'Off', isActive: false}
 *     ]} />
 *
 * ```
 *
 */
export const InputsSection = ({inputs, infoTooltip}: InputsSectionProps) => {
  const classes = useStyles();
  return (
    <div className={classes.container}>
      <div className={classes.titleRow}>
        <span className={classes.title}>
          INPUTS
        </span>
        {infoTooltip && (
          <InfoTooltip message={infoTooltip}
            size={24}
            color={dlsColor('grey3')} />
        )}
      </div>
      <div className={classes.rows}>
        {inputs.map(function(input) {
          return (
            <InputRow key={input.id}
              label={input.label}
              value={input.value}
              isActive={input.isActive} />
          );
        })}
      </div>
    </div>
  );
};

export default React.memo(InputsSection);
